import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { NoData, Spinner } from '@rio-cloud/rio-uikit';
import {
    fetchVehicleOccurrenceHistory,
    type OccurrencePersona,
    type VehicleOccurrenceHistory,
} from '../../../../../api/tas';
import { fetchUserRole } from '../../../../../api/fetchUser';
import { UserRoleEnum } from '../../../../../enums/role';
import { formatOccurrenceTypeLabel } from '../../../../../enums/occurrenceType';
import { formatOccurrenceStepLabel } from '../../../../../enums/occurrenceStep';

const columns = [
    'intl-msg:support.vehicleHistory.columns.occurrenceNumber',
    'intl-msg:support.vehicleHistory.columns.origin',
    'intl-msg:support.vehicleHistory.columns.occurrenceType',
    'intl-msg:support.vehicleHistory.columns.subject',
    'intl-msg:support.vehicleHistory.columns.customer',
    'intl-msg:support.vehicleHistory.columns.plateChassis',
    'intl-msg:support.vehicleHistory.columns.occurrenceKm',
    'intl-msg:support.vehicleHistory.columns.createdOn',
    'intl-msg:support.vehicleHistory.columns.step',
];

const personaByRole: Partial<Record<UserRoleEnum, OccurrencePersona>> = {
    [UserRoleEnum.TOWER]: 'TOWER',
    [UserRoleEnum.CONSULTANT]: 'CONSULTANT',
    [UserRoleEnum.GROUP_CONSULTANT]: 'CONSULTANT',
    [UserRoleEnum.MANAGER]: 'MANAGER',
    [UserRoleEnum.PARTNERSHIP]: 'PARTNERSHIP',
};

const routePrefixByPersona: Record<OccurrencePersona, string> = {
    TOWER: 'control-tower',
    CONSULTANT: 'dealership',
    MANAGER: 'fleet-manager',
    PARTNERSHIP: 'partnership',
};

const text = (value?: string | null) => (value && value !== 'undefined' && value !== 'null' ? value : '');

interface Props {
    chassis?: string;
}

export default function Occurrences({ chassis }: Props) {
    const intl = useIntl();
    const getStepLabel = (row: VehicleOccurrenceHistory) => {
        const currentStepId = row.stepTypeCurrent || row.occurrenceSteps?.find(step => !step.dtEnd)?.stepId;

        return currentStepId
            ? formatOccurrenceStepLabel(intl, currentStepId)
            : intl.formatMessage({ id: 'intl-msg:occurrence.card.completedStatus' });
    };
    const [persona, setPersona] = useState<OccurrencePersona | undefined>(undefined);
    const [rows, setRows] = useState<VehicleOccurrenceHistory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!chassis) {
            setRows([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        fetchUserRole()
            .then(role => {
                const userPersona = role ? personaByRole[role] : undefined;

                if (!userPersona) {
                    throw new Error(`Unsupported role for occurrence history: ${role}`);
                }

                setPersona(userPersona);
                return fetchVehicleOccurrenceHistory(chassis, userPersona);
            })
            .then(data => {
                if (!cancelled) {
                    setRows(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRows([]);
                    setHasError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [chassis]);

    if (isLoading) {
        return (
            <div className='display-flex justify-content-center padding-20'>
                <Spinner isDoubleSized />
            </div>
        );
    }

    if (hasError || !rows.length) {
        return (
            <NoData
                text={intl.formatMessage({
                    id: hasError
                        ? 'intl-msg:support.vehicleHistory.loadError'
                        : 'intl-msg:support.vehicleHistory.noData',
                })}
                className='text-size-16 padding-20'
            />
        );
    }

    return (
        <div className='table-responsive'>
            <table className='table table-head-filled whitespace-nowrap'>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th key={column}>{intl.formatMessage({ id: column })}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.uuid}>
                            <td>
                                {persona ? (
                                    <Link
                                        to={`/${routePrefixByPersona[persona]}/maintenance-monitoring?occurrence-uuid=${row.uuid}`}
                                    >
                                        {text(row.protocolNumber) || '-'}
                                    </Link>
                                ) : (
                                    text(row.protocolNumber) || '-'
                                )}
                            </td>
                            <td>{text(row.source)}</td>
                            <td>{formatOccurrenceTypeLabel(intl, row.occurrenceType) ?? text(row.occurrenceType)}</td>
                            <td>{text(row.subject)}</td>
                            <td>{text(row.customer)}</td>
                            <td>{text(row.vehicle?.chassis) || text(row.vehicle?.licensePlate)}</td>
                            <td>{row.vehicle?.odometer != null ? intl.formatNumber(row.vehicle.odometer) : ''}</td>
                            <td>{row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY HH:mm') : '-'}</td>
                            <td>{getStepLabel(row)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
